// theme.ts
import {
    defineConfig,
    defineTokens,
    defaultConfig,
    defineSlotRecipe,
    createSystem,
    mergeConfigs, defineRecipe,
} from '@chakra-ui/react'

const tokens = defineTokens({
    colors: {
        bg: {
            primary: '#161616',
            secondary: '#1A1A1A',
            highlight: '#333333',
            card: "#1F1F1F",
            drawer: '#242424',
            input: "#6B6B6B",
            inputLight: "#A3A3A3",
            inputActive: "#3F3F3F",
            chatTab: "#2B2B2B",
            chatFrame: "#191919",
            chatInput: "#2D2D2F",
            chatOwner: "#8700DB",
            chatBot: "#3F3F3F",
            tab: "#2F2F2F",
            dark: "#1B1B1B"
        },
        border: {
            primary: '#313131',
            secondary: '#727272',
            button: '#6A6A6A',
            inputLight: "#A3A3A3",
        },
        text: {
            menu: "#9199A9",
            user: "#959595",
            grey: '#898989',
            chatDate: "#727272"
        },
        brand: {
            50: '#e0f2ff',
            100: '#b3dfff',
            200: '#80ccff',
            300: '#4db8ff',
            400: '#1aa5ff',
            500: '#008be6',
            600: '#006bb4',
            700: '#004c82',
            800: '#002d51',
            900: '#001020',
        },
        chat: {
            icon: "#A424F4",
            iconDisabled: "rgba(164,36,244,0.50)",
            input: "#2D2D2F"
        }
    },
    fonts: {
        body: 'Inter, sans-serif',
        heading: 'Inter, sans-serif',
    },
})

const buttonRecipe = defineRecipe({
    variants: {
        variant: {
            primary: {
                bgGradient: 'to-r',
                gradientFrom: '#9D00FF',
                gradientVia: '#9000E9',
                gradientTo: '#8500D8',
                color: "white",
                fontSize: 16,
                fontWeight: 500,
                paddingX: 8,
                borderRadius: 8,
                textDecoration: 'unset',
                _hover: {
                    bgGradient: 'to-r',
                    gradientFrom: 'bg.highlight',
                    gradientVia: 'bg.highlight',
                    gradientTo: 'bg.highlight'
                }
            },
            outlined: {
                bg: "transparent",
                color: "white",
                fontSize: 16,
                fontWeight: 500,
                borderWidth: 1,
                paddingX: 8,
                borderRadius: 8,
                borderColor: 'border.button',
                _hover: {bg: "bg.highlight", color: "white"}
            },
            secondary: {bg: "green.500", color: "white"},
        },
    },
})

const inputRecipe = defineRecipe({
    variants: {
        variant: {
            primary: {
                bg: 'bg.inputActive',
                color: "white",
                fontSize: 18,
                fontWeight: 400,
                borderRadius: 8,
                py: 6,
                _focus: {borderWidth: 2, borderColor: 'border.inputLight'},
                _disabled: {bg: 'bg.inputLight'},
            },
            secondary: {
                bg: 'bg.inputLight',
                color: "white",
                fontSize: 18,
                fontWeight: 400,
                borderRadius: 8,
            },
            dark: {
                bg: 'bg.dark',
                color: "text.grey",
                padding: 2,
                fontSize: 14,
                fontWeight: 400,
                borderRadius: 8,
            },
        },
    },
})


const comboboxRecipe = defineSlotRecipe({
    className: "chakra-combobox",
    slots: ["root", "control", "input", "trigger", "content", "item"],
    base: {
        input: {
            width: "full",
            borderRadius: 8,
        },
    },
    variants: {
        visual: {
            ghosty: {
                control: {
                    borderRadius: 8,
                },
                input: {
                    px: 4,
                    py: 2,
                    bg: "bg.dark",
                    color: 'text.grey'
                },
                content: {
                    bg: "bg.tab",
                    boxShadow: "xs",
                    borderRadius: 8,
                    p: 1,
                },
                item: {
                    color: 'gray.400',
                    _hover: { bg: "bg.dark" },
                },
            },
            main: {
                control: {
                    borderRadius: 8,
                },
                input: {
                    px: 4,
                    py: 3,
                    color: 'text.grey'
                },
                content: {
                    bg: "bg.dark",
                    boxShadow: "xs",
                    borderRadius: 8,
                    p: 1,
                },
                item: {
                    color: 'gray.400',
                    _hover: { bg: "bg.dark" },
                },
            },
        },
    },
    defaultVariants: {
        visual: "ghosty",
    },
})

const selectRecipe = defineSlotRecipe({
    className: "chakra-select",
    slots: ["root", "control", "trigger", "content", "item", "valueText", "indicator"],
    base: {
        trigger: {
            width: "full",
            borderRadius: 8,
        },
    },
    variants: {
        visual: {
            ghosty: {
                trigger: {
                    px: 4,
                    py: 3.5,
                    bg: "bg.dark",
                    color: 'text.grey',
                    borderRadius: 8,
                    borderWidth: 0,
                    _hover: { bg: "bg.inputActive" },
                },
                content: {
                    bg: "bg.tab",
                    boxShadow: "xs",
                    borderRadius: 8,
                    p: 1,
                },
                item: {
                    color: 'gray.400',
                    borderRadius: 6,
                    _hover: { bg: "bg.dark" },
                },
                valueText: {
                    color: 'text.grey',
                },
                indicator: {
                    color: 'text.grey',
                },
            },
        },
    },
    defaultVariants: {
        visual: "ghosty",
    },
})

const iconRecipe = defineRecipe({
    variants: {
        variant: {
            hoverable: {
                padding: 2,
                transition: "background 0.2s ease-in-out",
                cursor: "pointer",
                _hover: {
                    bg: 'bg.inputActive',
                    borderRadius: 4,
                }
            },
        },
    },
})

const theme = defineConfig({
    theme: {
        tokens,
        recipes: {
            button: buttonRecipe,
            input: inputRecipe,
            icon: iconRecipe,
        },
        slotRecipes: {
            combobox: comboboxRecipe,
            select: selectRecipe,
        },
        keyframes: {
            bouncing: {
                '0%, 100%': {
                    transform: 'translateY(0px)'
                },
                '50%': {
                    transform: 'translateY(-8px)'
                }
            }
        }
    },
    globalCss: {
        "html, body": {
            bg: 'bg.primary',
            color: 'white',
        },
        "::selection": {
            bg: "purple.400 !important",
            color: "white !important",
        },
        "::-moz-selection": {
            bg: "purple.400 !important",
            color: "white !important",
        }
    },
})

const config = mergeConfigs(defaultConfig, theme)
export const system = createSystem(config)
